import databaseService from './database.service';

/**
 * Invoice Service for ZyloBill Web App
 * Adapted from desktop app to work with browser PouchDB
 */

export const invoiceService = {
  /* ═══════════════════════════════════════════════════════════
     INVOICE LIFECYCLE
     ═══════════════════════════════════════════════════════════ */

  /**
   * Create new invoice (draft)
   */
  async create(data) {
    const invoiceId = `invoice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const invoice = {
      _id: invoiceId,
      type: 'invoice',
      invoiceType: data.type || 'goods',
      status: 'draft',
      invoice_number: data.invoice_number || await this.generateInvoiceNumber(),
      issue_date: data.issue_date || new Date().toISOString().slice(0, 10),
      due_date: data.due_date || new Date().toISOString().slice(0, 10),
      payment_terms: data.payment_terms || 'due-on-receipt',
      client_id: null,
      items: [],
      discount: 0,
      discount_type: 'flat',
      shipping: 0,
      round_off: 0,
      subtotal: 0,
      tax_total: 0,
      total: 0,
      balance_due: 0,
      total_paid: 0,
      notes: '',
      deleted: false,
    };

    const result = await databaseService.createDocument(invoice);
    
    return {
      id: result.id,
      invoice_number: invoice.invoice_number,
      ...invoice
    };
  },

  /**
   * Generate next invoice number
   */
  async generateInvoiceNumber() {
    const invoices = await databaseService.getInvoices();
    const numbers = invoices
      .map(inv => {
        const match = inv.invoice_number?.match(/INV-(\d+)/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return `INV-${String(nextNumber).padStart(4, '0')}`;
  },

  /**
   * Autosave invoice (main save function)
   * Handles header + items + calculations
   * Includes conflict resolution with retry logic
   */
  async save(invoiceId, data, retryCount = 0) {
    const MAX_RETRIES = 3;
    
    try {
      const currentInvoice = await databaseService.readDocument(invoiceId);
      
      if (!currentInvoice) {
        throw new Error('Invoice not found');
      }

      // Calculate totals
      const calculations = this.calculateTotals(data.items || [], {
        discount: data.discount || 0,
        discountType: data.discountType || 'flat',
        shipping: data.shipping || 0,
        roundOff: data.roundOff || 0,
      });

      const updatedInvoice = {
        ...currentInvoice,
        invoice_number: data.invoiceNumber || currentInvoice.invoice_number,
        invoiceType: data.type || currentInvoice.invoiceType,
        client_id: data.client?.id || currentInvoice.client_id,
        client_name: data.client?.name || currentInvoice.client_name,
        client_email: data.client?.email || currentInvoice.client_email,
        client_phone: data.client?.phone || currentInvoice.client_phone,
        client_gstin: data.client?.gstin || currentInvoice.client_gstin,
        client_billing_address: data.client?.billing_address || currentInvoice.client_billing_address,
        client_city: data.client?.city || currentInvoice.client_city,
        client_state: data.client?.state || currentInvoice.client_state,
        client_pincode: data.client?.pincode || currentInvoice.client_pincode,
        issue_date: data.issueDate || currentInvoice.issue_date,
        due_date: data.dueDate || currentInvoice.due_date,
        po_date: data.poDate || currentInvoice.po_date,
        agreement_number: data.agreementNumber || currentInvoice.agreement_number,
        po_number: data.poNumber || currentInvoice.po_number,
        payment_terms: data.paymentTerms || currentInvoice.payment_terms,
        discount: data.discount || 0,
        discount_type: data.discountType || 'flat',
        shipping: data.shipping || 0,
        round_off: data.roundOff || 0,
        items: (data.items || []).map(item => ({
          id: item.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: item.name,
          description: item.description || '',
          type: item.type || 'goods',
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          discount: item.discount || 0,
          discount_type: item.discountType || 'flat',
          gst_rate: item.gstRate || 0,
          gst_type: item.gstType || 'none',
          hsn: item.hsn || '',
          sac: item.sac || '',
          uom: item.uom || 'unit',
          source_item_id: item.sourceItemId,
          is_overridden: item.isOverridden || false,
        })),
        notes: data.notes || currentInvoice.notes || '',
        status: data.status || currentInvoice.status,
        ...calculations,
      };

      await databaseService.updateDocument(updatedInvoice);
      
      return {
        ...updatedInvoice,
        id: updatedInvoice._id,
      };
    } catch (error) {
      // Handle 409 conflict error with retry
      if (error.message?.includes('conflict') && retryCount < MAX_RETRIES) {
        console.warn(`[Invoice Service] Document conflict detected, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        
        // Wait a bit before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, retryCount)));
        
        // Retry with fresh document
        return this.save(invoiceId, data, retryCount + 1);
      }
      
      // Re-throw error if not a conflict or max retries reached
      throw error;
    }
  },

  /**
   * Calculate invoice totals
   */
  calculateTotals(items, options = {}) {
    let subtotal = 0;
    let taxTotal = 0;

    items.forEach(item => {
      const quantity = item.quantity || 0;
      const rate = item.rate || 0;
      const itemDiscount = item.discount || 0;
      const itemDiscountType = item.discountType || 'flat';
      
      // Calculate item amount before tax
      let itemAmount = quantity * rate;
      
      // Apply item discount
      if (itemDiscountType === 'percentage') {
        itemAmount -= (itemAmount * itemDiscount) / 100;
      } else {
        itemAmount -= itemDiscount;
      }
      
      subtotal += itemAmount;
      
      // Calculate tax
      const gstRate = item.gstRate || 0;
      const itemTax = (itemAmount * gstRate) / 100;
      taxTotal += itemTax;
    });

    // Apply invoice-level discount
    const discount = options.discount || 0;
    const discountType = options.discountType || 'flat';
    
    if (discountType === 'percentage') {
      subtotal -= (subtotal * discount) / 100;
    } else {
      subtotal -= discount;
    }

    // Add shipping
    const shipping = options.shipping || 0;
    subtotal += shipping;

    // Calculate total
    let total = subtotal + taxTotal;

    // Apply round off
    const roundOff = options.roundOff || 0;
    total += roundOff;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax_total: Math.round(taxTotal * 100) / 100,
      total: Math.round(total * 100) / 100,
      balance_due: Math.round(total * 100) / 100,
    };
  },

  /**
   * Send invoice (finalize and lock)
   */
  async send(invoiceId, data = {}) {
    const currentInvoice = await databaseService.readDocument(invoiceId);
    
    if (!currentInvoice) {
      throw new Error('Invoice not found');
    }

    if (currentInvoice.status !== 'draft') {
      throw new Error('Only draft invoices can be sent');
    }

    // Validate invoice
    if (!currentInvoice.client_id) {
      throw new Error('Client is required');
    }

    if (!currentInvoice.items || currentInvoice.items.length === 0) {
      throw new Error('At least one line item is required');
    }

    // Update status to sent
    const updatedInvoice = {
      ...currentInvoice,
      status: 'sent',
      sent_at: new Date().toISOString(),
    };

    await databaseService.updateDocument(updatedInvoice);

    // Try to send email
    let emailResult = {
      skipped: true,
      message: 'Email functionality not yet implemented in web version'
    };

    try {
      console.log('[Invoice Service] Attempting to send email for invoice:', invoiceId);
      
      // Import email service dynamically to avoid circular dependencies
      const { sendInvoiceEmail } = await import('./email.service');
      
      // Get company settings and theme for email and PDF
      const settingsService = await import('./settings.service');
      const theme = await settingsService.default.getActiveTheme();
      const companySettings = theme?.company || {};
      
      // Fetch payments (will be empty for newly sent invoices, but included for consistency)
      let payments = [];
      try {
        payments = await this.listPayments(invoiceId);
      } catch (err) {
        console.warn('[Invoice Service] Could not fetch payments for email:', err);
      }
      
      console.log('[Invoice Service] Invoice data:', {
        id: updatedInvoice._id,
        invoice_number: updatedInvoice.invoice_number,
        client_id: updatedInvoice.client_id,
        client_name: updatedInvoice.client_name,
        client_email: updatedInvoice.client_email,
        payments_count: payments.length
      });
      
      console.log('[Invoice Service] Backend will generate PDF automatically');
      
      // Send email - backend will generate PDF attachment
      const result = await sendInvoiceEmail(
        {
          ...updatedInvoice,
          id: updatedInvoice._id,
          payments: payments || [], // Include payments for PDF generation
          client: {
            email: updatedInvoice.client_email,
            name: updatedInvoice.client_name
          },
          invoiceNumber: updatedInvoice.invoice_number,
          issueDate: updatedInvoice.issue_date,
          total: updatedInvoice.total
        },
        companySettings,
        null // No PDF base64 needed - backend generates it
      );
      
      console.log('[Invoice Service] Email send result:', result);
      emailResult = result;
    } catch (emailError) {
      console.error('[Invoice Service] Email sending failed:', emailError);
      emailResult = {
        success: false,
        message: emailError.message || 'Failed to send email'
      };
    }
    
    return {
      ...updatedInvoice,
      id: updatedInvoice._id,
      status: 'sent',
      emailResult
    };
  },

  /**
   * Resend invoice (send email again for already sent invoice)
   */
  async resend(invoiceId) {
    const currentInvoice = await databaseService.readDocument(invoiceId);
    
    if (!currentInvoice) {
      throw new Error('Invoice not found');
    }

    // Validate invoice must be sent or paid to resend
    if (currentInvoice.status !== 'sent' && currentInvoice.status !== 'paid') {
      throw new Error('Only sent or paid invoices can be resent');
    }

    // Validate invoice has client
    if (!currentInvoice.client_id) {
      throw new Error('Client is required to resend invoice');
    }

    console.log('[Invoice Service] Resending invoice:', invoiceId);

    // Try to send email
    let emailResult = {
      skipped: true,
      message: 'Email functionality not yet implemented in web version'
    };

    try {
      console.log('[Invoice Service] Attempting to resend email for invoice:', invoiceId);
      
      // Import email service dynamically to avoid circular dependencies
      const { sendInvoiceEmail } = await import('./email.service');
      
      // Get company settings and theme for email and PDF
      const settingsService = await import('./settings.service');
      const theme = await settingsService.default.getActiveTheme();
      const companySettings = theme?.company || {};
      
      // Fetch payments for sent/paid invoices
      let payments = [];
      try {
        payments = await this.listPayments(invoiceId);
        console.log('[Invoice Service] Fetched payments for resend:', payments.length);
      } catch (err) {
        console.warn('[Invoice Service] Could not fetch payments for resend email:', err);
      }
      
      console.log('[Invoice Service] Invoice data for resend:', {
        id: currentInvoice._id,
        invoice_number: currentInvoice.invoice_number,
        client_id: currentInvoice.client_id,
        client_name: currentInvoice.client_name,
        client_email: currentInvoice.client_email,
        status: currentInvoice.status,
        total_paid: currentInvoice.total_paid,
        payments_count: payments.length
      });
      
      console.log('[Invoice Service] Backend will generate PDF automatically');
      
      // Send email - backend will generate PDF attachment with payment info
      const result = await sendInvoiceEmail(
        {
          ...currentInvoice,
          id: currentInvoice._id,
          payments: payments || [], // Include payments for PDF generation
          client: {
            email: currentInvoice.client_email,
            name: currentInvoice.client_name
          },
          invoiceNumber: currentInvoice.invoice_number,
          issueDate: currentInvoice.issue_date,
          total: currentInvoice.total
        },
        companySettings,
        null // No PDF base64 needed - backend generates it
      );
      
      console.log('[Invoice Service] Email resend result:', result);
      emailResult = result;
    } catch (emailError) {
      console.error('[Invoice Service] Email resending failed:', emailError);
      emailResult = {
        success: false,
        message: emailError.message || 'Failed to resend email'
      };
    }
    
    return {
      ...currentInvoice,
      id: currentInvoice._id,
      emailResult
    };
  },

  /**
   * Get invoice by ID
   */
  async get(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      return null;
    }

    return {
      ...invoice,
      id: invoice._id,
    };
  },

  /**
   * List all invoices with optional filters
   */
  async list(filters = {}) {
    const invoices = await databaseService.getInvoices();
    
    // Apply filters
    let filtered = invoices;
    
    // Filter by archived status
    if (filters.archived !== undefined) {
      if (filters.archived) {
        // Show only archived invoices
        filtered = filtered.filter(inv => inv.archived_at);
      } else {
        // Show only non-archived invoices
        filtered = filtered.filter(inv => !inv.archived_at);
      }
    }
    
    // Filter by status (if not "all")
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(inv => inv.status === filters.status);
    }
    
    // Filter by client
    if (filters.client_id) {
      filtered = filtered.filter(inv => inv.client_id === filters.client_id);
    }
    
    // Filter by search query
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(inv => 
        (inv.invoice_number || '').toLowerCase().includes(search) ||
        (inv.client_name || '').toLowerCase().includes(search)
      );
    }

    // Sort
    const sortBy = filters.sortBy || 'updated_at';
    const sortOrder = filters.sortOrder || 'DESC';
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'invoice_number':
          aVal = a.invoice_number || '';
          bVal = b.invoice_number || '';
          break;
        case 'client_name':
          aVal = a.client_name || '';
          bVal = b.client_name || '';
          break;
        case 'total':
          aVal = a.total || 0;
          bVal = b.total || 0;
          break;
        case 'issue_date':
          aVal = new Date(a.issue_date || 0);
          bVal = new Date(b.issue_date || 0);
          break;
        case 'updated_at':
        default:
          aVal = new Date(a.metadata?.updatedAt || a.metadata?.createdAt || 0);
          bVal = new Date(b.metadata?.updatedAt || b.metadata?.createdAt || 0);
          break;
      }
      
      if (sortOrder === 'ASC') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered.map(inv => ({
      ...inv,
      id: inv._id,
      updated_at: inv.metadata?.updatedAt || inv.metadata?.createdAt,
      created_at: inv.metadata?.createdAt,
    }));
  },

  /**
   * Delete invoice (draft only)
   */
  async delete(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    if (invoice.status !== 'draft') {
      throw new Error('Only draft invoices can be deleted');
    }

    await databaseService.deleteDocument(id);
    
    return { success: true };
  },

  /**
   * Archive invoice
   */
  async archive(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    const updatedInvoice = {
      ...invoice,
      archived_at: new Date().toISOString(),
      metadata: {
        ...invoice.metadata,
        updatedAt: new Date().toISOString()
      }
    };

    await databaseService.updateDocument(updatedInvoice);
    
    return {
      ...updatedInvoice,
      id: updatedInvoice._id,
      success: true
    };
  },

  /**
   * Unarchive invoice
   */
  async unarchive(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    const updatedInvoice = {
      ...invoice,
      archived_at: null,
      metadata: {
        ...invoice.metadata,
        updatedAt: new Date().toISOString()
      }
    };

    await databaseService.updateDocument(updatedInvoice);
    
    return {
      ...updatedInvoice,
      id: updatedInvoice._id,
      success: true
    };
  },

  /**
   * Permanently delete archived invoice
   */
  async deleteArchived(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    if (!invoice.archived_at) {
      throw new Error('Only archived invoices can be permanently deleted');
    }

    await databaseService.deleteDocument(id);
    
    return { success: true };
  },

  /**
   * Execute invoice action (unified action handler)
   * Supports: delete, send, resend, duplicate, markPaid, sendReminder, cancel
   */
  async executeAction(invoiceId, action, data = {}) {
    switch (action) {
      case 'delete':
        return await this.delete(invoiceId);
      
      case 'send':
        return await this.send(invoiceId, data);
      
      case 'resend':
        return await this.resend(invoiceId);
      
      case 'duplicate':
        return await this.duplicate(invoiceId);
      
      case 'markPaid':
        return await this.markPaid(invoiceId, data);
      
      case 'sendReminder':
        return await this.sendReminder(invoiceId);
      
      case 'cancel':
        return await this.cancel(invoiceId);
      
      default:
        throw new Error(`Unknown action: ${action}`);
    }
  },

  /**
   * Duplicate invoice
   */
  async duplicate(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    // Generate new invoice number
    const newInvoiceNumber = await this.generateInvoiceNumber();
    
    // Create duplicate
    const duplicateInvoice = {
      ...invoice,
      _id: `invoice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      _rev: undefined, // Remove revision
      invoice_number: newInvoiceNumber,
      status: 'draft',
      sent_at: null,
      archived_at: null,
      total_paid: 0,
      balance_due: invoice.total,
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };

    const result = await databaseService.createDocument(duplicateInvoice);
    
    return {
      id: result.id,
      invoice_number: newInvoiceNumber,
      ...duplicateInvoice
    };
  },

  /**
   * Mark invoice as paid
   */
  async markPaid(id, paymentData = {}) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    // Add payment for full amount
    await this.addPayment(id, {
      amount: invoice.balance_due || invoice.total,
      payment_date: paymentData.payment_date || new Date().toISOString().slice(0, 10),
      mode: paymentData.mode || 'other',
      notes: paymentData.notes || 'Marked as paid'
    });

    return { success: true };
  },

  /**
   * Send payment reminder
   */
  async sendReminder(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    if (invoice.status === 'paid') {
      throw new Error('Cannot send reminder for paid invoice');
    }

    // TODO: Implement email reminder via backend API
    // For MVP: Email functionality can be added later
    
    return {
      success: true,
      message: 'Email reminder functionality not yet implemented in web version'
    };
  },

  /**
   * Cancel invoice
   */
  async cancel(id) {
    const invoice = await databaseService.readDocument(id);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    if (invoice.status === 'paid') {
      throw new Error('Cannot cancel paid invoice');
    }

    const updatedInvoice = {
      ...invoice,
      status: 'cancelled',
      metadata: {
        ...invoice.metadata,
        updatedAt: new Date().toISOString()
      }
    };

    await databaseService.updateDocument(updatedInvoice);
    
    return {
      ...updatedInvoice,
      id: updatedInvoice._id,
      success: true
    };
  },

  /* ═══════════════════════════════════════════════════════════
     LEGACY METHODS (for backward compatibility)
     ═══════════════════════════════════════════════════════════ */

  async add(data) {
    return this.create(data);
  },

  async update(id, data) {
    return this.save(id, data);
  },

  /* ═══════════════════════════════════════════════════════════
     ITEMS
     ═══════════════════════════════════════════════════════════ */

  async getItems(invoiceId) {
    const invoice = await this.get(invoiceId);
    return invoice?.items || [];
  },

  /* ═══════════════════════════════════════════════════════════
     PAYMENTS
     ═══════════════════════════════════════════════════════════ */

  /**
   * List all payments for an invoice
   */
  async listPayments(invoiceId) {
    const allPayments = await databaseService.getPayments();
    const invoicePayments = allPayments.filter(p => 
      p.invoice_id === invoiceId && !p.deleted
    );
    
    return invoicePayments.map(payment => ({
      ...payment,
      id: payment._id,
    }));
  },

  /**
   * Add payment to invoice
   */
  async addPayment(invoiceId, paymentData) {
    // Get invoice
    const invoice = await databaseService.readDocument(invoiceId);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    // Create payment document
    const paymentId = `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const payment = {
      _id: paymentId,
      type: 'payment',
      invoice_id: invoiceId,
      amount: paymentData.amount,
      payment_date: paymentData.payment_date || paymentData.paymentDate || new Date().toISOString().slice(0, 10),
      mode: paymentData.mode || 'other',
      notes: paymentData.notes || '',
      deleted: false,
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };

    // Save payment
    const result = await databaseService.createDocument(payment);

    // Update invoice balance
    const allPayments = await databaseService.getPayments();
    const invoicePayments = allPayments.filter(p => 
      p.invoice_id === invoiceId && !p.deleted
    );
    const totalPaid = invoicePayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    
    const updatedInvoice = {
      ...invoice,
      total_paid: totalPaid,
      balance_due: (Number(invoice.total) || 0) - totalPaid,
      status: totalPaid >= (Number(invoice.total) || 0) ? 'paid' : 'sent',
      metadata: {
        ...invoice.metadata,
        updatedAt: new Date().toISOString()
      }
    };

    await databaseService.updateDocument(updatedInvoice);

    return {
      id: result.id,
      success: true,
    };
  },

  /**
   * Delete payment
   */
  async deletePayment(paymentId) {
    // Get payment
    const payment = await databaseService.readDocument(paymentId);
    
    if (!payment) {
      throw new Error('Payment not found');
    }

    const invoiceId = payment.invoice_id;

    // Delete payment
    await databaseService.deleteDocument(paymentId);

    // Update invoice balance
    const invoice = await databaseService.readDocument(invoiceId);
    if (invoice) {
      const allPayments = await databaseService.getPayments();
      const invoicePayments = allPayments.filter(p => 
        p.invoice_id === invoiceId && !p.deleted
      );
      const totalPaid = invoicePayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      
      const updatedInvoice = {
        ...invoice,
        total_paid: totalPaid,
        balance_due: (Number(invoice.total) || 0) - totalPaid,
        status: totalPaid >= (Number(invoice.total) || 0) ? 'paid' : 'sent',
        metadata: {
          ...invoice.metadata,
          updatedAt: new Date().toISOString()
        }
      };

      await databaseService.updateDocument(updatedInvoice);
    }

    return { success: true };
  },

  /* ═══════════════════════════════════════════════════════════
     PDF / PRINT (Not implemented for web MVP)
     ═══════════════════════════════════════════════════════════ */

  async print(invoice) {
    // TODO: Implement browser-based PDF generation
    // Options:
    // 1. Use jsPDF or pdfmake
    // 2. Call backend API to generate PDF
    // 3. Use html2pdf to convert preview to PDF
    throw new Error('PDF generation not yet implemented in web version');
  },
};